/**
 * Interest Selector Component - Modern, searchable, categorized selection
 */

import React, { useState, useMemo } from 'react';
import type { InterestOption } from '../lib/pb';

interface InterestSelectorProps {
  options: InterestOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  error?: string;
}

/**
 * Map category values to display names
 */
const CATEGORY_LABELS: Record<string, string> = {
  'erp-business': 'ERP & Business Management',
  collaboration: 'Collaboration & Communication',
  devops: 'DevOps & Development',
  data: 'Data & Analytics',
  infrastructure: 'Infrastructure & Security',
  automation: 'Automation & Workflows',
  content: 'Content & Documentation',
  media: 'Media & Entertainment',
  ai: 'AI & Machine Learning',
  other: 'Other',
};

/**
 * Helper function to get category display name
 */
function getCategoryDisplayName(option: InterestOption): string {
  const category = option.category || 'other';
  return CATEGORY_LABELS[category] || 'Other';
}

/**
 * Group options by category
 */
function groupByCategory(
  options: InterestOption[]
): Record<string, InterestOption[]> {
  return options.reduce(
    (acc, option) => {
      const category = getCategoryDisplayName(option);
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(option);
      return acc;
    },
    {} as Record<string, InterestOption[]>
  );
}

export const InterestSelector: React.FC<InterestSelectorProps> = ({
  options,
  selected,
  onChange,
  disabled,
  error,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter and group options
  const { filteredOptions, categories } = useMemo(() => {
    const filtered = searchQuery
      ? options.filter(option =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;

    const grouped = groupByCategory(filtered);
    const sortedCategories = Object.keys(grouped).sort();

    return {
      filteredOptions: filtered,
      categories: sortedCategories.map(cat => ({
        name: cat,
        items: grouped[cat],
      })),
    };
  }, [options, searchQuery]);

  const handleToggle = (value: string) => {
    if (disabled) return;

    if (selected.includes(value)) {
      onChange(selected.filter(item => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleRemoveSelected = (value: string) => {
    if (disabled) return;
    onChange(selected.filter(item => item !== value));
  };

  const selectedOptions = options.filter(opt => selected.includes(opt.value));
  const hasSelection = selected.length > 0;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`
          w-full px-4 py-3 border rounded-lg text-left
          transition-all duration-200
          ${error ? 'border-red-500' : 'border-neutral-300'}
          ${disabled ? 'bg-neutral-50 cursor-not-allowed' : 'bg-white cursor-pointer hover:border-primary-500'}
          focus:outline-none focus:ring-2 focus:ring-primary-500
        `}
      >
        <div className="flex items-center justify-between">
          <span className="text-neutral-700">
            {hasSelection ? (
              <span className="text-neutral-900 font-medium">
                {selected.length}{' '}
                {selected.length === 1 ? 'interest' : 'interests'} selected
              </span>
            ) : (
              <span className="text-neutral-500">Select areas of interest</span>
            )}
          </span>
          <svg
            className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Selected Chips */}
      {hasSelection && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedOptions.map(option => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 text-primary-700 rounded-md text-sm"
            >
              {option.label}
              <button
                type="button"
                onClick={() => handleRemoveSelected(option.value)}
                disabled={disabled}
                className="hover:text-primary-900 transition-colors disabled:opacity-50"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-2 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-96 overflow-hidden"
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {/* Search Input */}
          <div className="p-4 border-b border-neutral-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search interests..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-80">
            {filteredOptions.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                <p>No interests found matching "{searchQuery}"</p>
              </div>
            ) : (
              <div className="p-2">
                {categories.map(category => (
                  <div key={category.name} className="mb-4 last:mb-0">
                    <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide px-3 py-2">
                      {category.name}
                    </h4>
                    <div className="space-y-1">
                      {category.items.map(option => {
                        const isSelected = selected.includes(option.value);
                        return (
                          <label
                            key={option.id}
                            className="flex items-center px-3 py-2 rounded-md hover:bg-neutral-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggle(option.value)}
                              className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                            />
                            <span className="ml-3 text-sm text-neutral-700 flex-1">
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-neutral-200 bg-neutral-50">
            <p className="text-xs text-neutral-600">
              {selected.length > 0 ? (
                <>
                  {selected.length}{' '}
                  {selected.length === 1 ? 'interest' : 'interests'} selected
                </>
              ) : (
                'Select one or more interests'
              )}
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-red-600 text-sm mt-1" role="alert">
          {error}
        </p>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
